import React, {useState} from "react";
import {VerifactuRecordList, VerifactuRestControllerApiFactory} from "arteco-api-client-ts";
import AppList, {AppListConfig} from "../../../../components/layout/list";
import {TableCell, Tooltip, Typography} from "@mui/material";
import {SoftChip, Tone} from "../../../../components/kit";
import {useApiManager} from "../../../../components/utils/api";
import {dateToString} from "../../../../components/utils/dates";
import {FormButtonProps} from "../../../../components/form/form_inputs/form_button";

/**
 * Los registros VERI*FACTU y su estado en la AEAT (#13098).
 *
 * Hasta ahora todo esto solo se veía por API, y un rechazo que hay que ir a buscar no se ve.
 */

const estadoColor: Record<string, Tone> = {
    PENDIENTE: "warning",
    ACEPTADO: "success",
    ACEPTADO_CON_ERRORES: "info",
    RECHAZADO: "error",
};

// Etiqueta legible del estado; el valor del API se queda como clave (#13879).
const estadoLabel: Record<string, string> = {
    PENDIENTE: "Pendiente",
    ACEPTADO: "Aceptado",
    ACEPTADO_CON_ERRORES: "Aceptado con errores",
    RECHAZADO: "Rechazado",
};

export const VerifactuListPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(VerifactuRestControllerApiFactory);
    const [trabajando, setTrabajando] = useState(false);
    const hoy = new Date();

    const lanzar = (promesa: Promise<any>, exito: string) => {
        setTrabajando(true);
        apiManager.execute(promesa, exito).finally(() => setTrabajando(false));
    };

    const listConfig: AppListConfig<VerifactuRecordList> = {
        title: "Registros VERI*FACTU",
        // No se entra en el detalle: un registro no se edita, es inmutable por definición.
        onClick: "",
        headers: [
            {title: "Factura", width: 110},
            {title: "Fecha", width: 100},
            {title: "Tipo", width: 110},
            {title: "Estado", width: 150},
            {title: "Error", width: 320},
            {title: "CSV", width: 160},
            {title: "Huella", width: 120},
        ],
        actions: [
            {
                label: trabajando ? "…" : "Enviar pendientes",
                action: () => lanzar(ctrl.sendVerifactuRecord(100), "Enviado"),
            } as FormButtonProps,
            {
                label: trabajando ? "…" : "Subsanar rechazados",
                action: () => lanzar(ctrl.subsanarVerifactuRecord(100),
                                     "Subsanaciones generadas; salen en el siguiente envío"),
            } as FormButtonProps,
            {
                // Resuelve lo que quedó PENDIENTE porque un envío se cortó: pregunta a la AEAT
                // qué tiene de ese mes en vez de reenviar a ciegas.
                label: trabajando ? "…" : `Conciliar ${hoy.getMonth() + 1}/${hoy.getFullYear()}`,
                action: () => lanzar(ctrl.conciliarVerifactuRecord(hoy.getFullYear(), hoy.getMonth() + 1),
                                     "Conciliado con la AEAT"),
            } as FormButtonProps,
            {
                label: "Comprobar cadena",
                action: () => apiManager.executeRaw(ctrl.verifyChainVerifactuRecord()).then(resp => {
                    const problemas = resp.data as unknown as string[];
                    alert(problemas.length === 0
                              ? "La cadena de huellas está íntegra."
                              : "Problemas en la cadena:\n\n" + problemas.join("\n"));
                }),
            } as FormButtonProps,
            {
                label: "Certificado",
                action: () => apiManager.executeRaw(ctrl.certificadoVerifactuRecord()).then(resp => {
                    const estado = resp.data as any;
                    // El subject viene como DN con OIDs en hexadecimal; de ahí solo interesan
                    // el titular y la organización.
                    const trozo = (dn: string, clave: string) =>
                        (new RegExp(`(?:^|,)${clave}=([^,]+)`).exec(dn ?? "") ?? [])[1] ?? "";
                    const titular = [trozo(estado?.subject, "CN"), trozo(estado?.subject, "O")]
                        .filter(Boolean).join(" · ");
                    alert(estado?.error
                              ? "No se puede leer el certificado:\n\n" + estado.error
                              : `Caduca el ${estado?.caduca}\n\n${titular}`);
                }),
            } as FormButtonProps,
        ],
        decorator: (bean: VerifactuRecordList) => (
            <>
                <TableCell><Typography variant="body2" fontWeight={600}>{bean.invoiceNumber}</Typography></TableCell>
                <TableCell sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(bean.invoiceDate)}</TableCell>
                <TableCell>
                    <SoftChip label={bean.recordType === "ANULACION" ? "Anulación" : (bean.subsanacion ? "Subsanación" : "Alta")}
                              tone={bean.recordType === "ANULACION" ? "warning" : "neutral"}/>
                </TableCell>
                <TableCell>
                    <SoftChip label={estadoLabel[bean.submissionStatus ?? "PENDIENTE"] ?? bean.submissionStatus}
                              tone={estadoColor[bean.submissionStatus ?? "PENDIENTE"] ?? "neutral"}/>
                </TableCell>
                <TableCell>
                    {bean.errorCode ? (
                        <Typography variant="caption">
                            <strong>{bean.errorCode}</strong> {bean.errorDescription}
                        </Typography>
                    ) : ""}
                </TableCell>
                <TableCell><Typography variant="caption" sx={{fontFamily: "monospace"}}>{bean.csv}</Typography></TableCell>
                <TableCell>
                    <Tooltip title={bean.invoiceHash ?? ""}>
                        <Typography variant="caption" sx={{fontFamily: "monospace"}}>{(bean.invoiceHash ?? "").substring(0, 12)}…</Typography>
                    </Tooltip>
                </TableCell>
            </>
        ),
        supplier: async (currentPage, size, term, showDismissed) => {
            return ctrl.pageVerifactuRecord(currentPage, size, undefined, undefined, undefined, undefined, term, showDismissed);
        },
    };

    // Sin «Añadir»: un registro VERI*FACTU no se crea a mano, lo genera el sistema al expedir.
    return <AppList config={listConfig} appendable={false}/>;
};
